import { snakeCase } from 'change-case';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsMeta } from '../AnalyticsMeta';
import { useAnalyticsProxyContext } from '../AnalyticsProxyProvider';
import type { AnalyticsEvent, AnalyticsEventProps } from '../types/Analytics';
import { UNSET_ENTRY_POINT } from './constants';
import type { TrackEventOptions } from './types';

const useAnalytics = () => {
  const {
    trackPageView: ctxTrackPageView,
    trackEvent: ctxTrackEvent,
    identifyUser: ctxIdentifyUser,
    resetUserIdentity: ctxResetUserIdentity,
    initialize: ctxInitialize,
    debuggingEnabled: ctxDebuggingEnabled,
  } = useAnalyticsProxyContext();
  const { entryPoint: ctxEntryPoint, additionalEventProps: ctxEventProps } = useAnalyticsMeta();

  const debuggingEnabledRef = useRef(ctxDebuggingEnabled);
  useEffect(() => {
    debuggingEnabledRef.current = ctxDebuggingEnabled;
  }, [ctxDebuggingEnabled]);

  const trackPageView = useCallback(
    (pathOverride?: string) => {
      ctxTrackPageView({
        path: pathOverride ?? window.location.pathname,
      });
    },
    [ctxTrackPageView],
  );

  const trackEvent = useCallback(
    <E extends AnalyticsEvent>(
      event: E,
      ...[propsWithInaccurateType, options]: AnalyticsEventProps[E] extends undefined
        ? [props?: AnalyticsEventProps[E] | undefined, options?: TrackEventOptions]
        : [props: AnalyticsEventProps[E] | undefined, options?: TrackEventOptions]
    ) => {
      const props: AnalyticsEventProps[E] | undefined = propsWithInaccurateType;
      const entryPoint = options?.overrideEntryPoint ?? ctxEntryPoint;
      const propsWithGlobals = {
        ...(props ?? {}),
        ...ctxEventProps,
        entryPoint:
          entryPoint && entryPoint !== UNSET_ENTRY_POINT ? snakeCase(entryPoint) : undefined,
      };

      if (debuggingEnabledRef.current) {
        /* eslint-disable no-console */
        console.groupCollapsed('[Analytics] Tracking event:', event);
        if (options) console.log('[Analytics] Tracking options:', options);
        if (propsWithGlobals.entryPoint) {
          console.log('[Analytics] Entry point:', propsWithGlobals.entryPoint);
        }
        if (props) {
          console.log('[Analytics] Props:');
          console.table(props);
        }
        if ('experiments' in propsWithGlobals) {
          console.log('[Analytics] Active experiments:');
          console.table(propsWithGlobals.experiments);
        }
        console.groupEnd();
        /* eslint-enable no-console */
      }

      ctxTrackEvent(event, propsWithGlobals);
    },
    [ctxEntryPoint, ctxEventProps, ctxTrackEvent],
  );

  return useMemo(
    () => ({
      trackEvent,
      trackPageView,
      /** @deprecated use trackPageView instead */
      trackPageview: trackPageView,
      trackUserIdentity: ctxIdentifyUser,
      resetUserIdentity: ctxResetUserIdentity,
      initialize: ctxInitialize,
    }),
    [ctxIdentifyUser, ctxInitialize, ctxResetUserIdentity, trackEvent, trackPageView],
  );
};

export default useAnalytics;
